import {
  ContractStatus,
  EligibilityAssessment,
  EligibilityAssessmentStatus,
  EligibilityRuleAvaluationStatus,
  InstallmentStatus,
  InvoiceStatus,
  Order,
  OrderDocumentType,
  OrderList,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  ShipmentStatus,
} from './order.model';
import { LateFeeType } from './late-fee-policy.model';
import { CustomerOrder } from './customer.model';

type EnumObject<E extends number> = Record<string, string | E>;

/**
 * The API serializes enums by name while the models declare numeric enums.
 * Converts a name (or an already numeric value) into the numeric enum member.
 */
function toEnum<E extends number>(
  enumObject: EnumObject<E>,
  value: unknown,
): E {
  if (typeof value === 'string') return enumObject[value] as E;
  return value as E;
}

function mapAssessment(assessment: EligibilityAssessment | null | undefined) {
  if (!assessment) return assessment;

  return {
    ...assessment,
    status: toEnum(EligibilityAssessmentStatus, assessment.status),
    rulesEvaluations: assessment.rulesEvaluations?.map((rule) => ({
      ...rule,
      status: toEnum(EligibilityRuleAvaluationStatus, rule.status),
    })),
  };
}

export function mapOrderList(item: OrderList): OrderList {
  return { ...item, status: toEnum(OrderStatus, item.status) };
}

export function mapCustomerOrder(order: CustomerOrder): CustomerOrder {
  return { ...order, status: toEnum(OrderStatus, order.status) };
}

export function mapOrder(order: Order): Order {
  return {
    ...order,
    status: toEnum(OrderStatus, order.status),
    histories: order.histories?.map((history) => ({
      ...history,
      from: toEnum(OrderStatus, history.from),
      to: toEnum(OrderStatus, history.to),
    })),
    eligibilityAssessment: mapAssessment(order.eligibilityAssessment) ?? null,
    documents: order.documents?.map((document) => ({
      ...document,
      type: toEnum(OrderDocumentType, document.type),
    })),
    shipments: order.shipments?.map((shipment) => ({
      ...shipment,
      status: toEnum(ShipmentStatus, shipment.status),
      histories: shipment.histories?.map((history) => ({
        ...history,
        from: toEnum(ShipmentStatus, history.from),
        to: toEnum(ShipmentStatus, history.to),
      })),
    })),
    contract: order.contract && {
      ...order.contract,
      status: toEnum(ContractStatus, order.contract.status),
    },
    paymentPlan: order.paymentPlan && {
      ...order.paymentPlan,
      lateFeeType:
        order.paymentPlan.lateFeeType == null
          ? order.paymentPlan.lateFeeType
          : toEnum(LateFeeType, order.paymentPlan.lateFeeType),
      installments: order.paymentPlan.installments?.map((installment) => ({
        ...installment,
        status: toEnum(InstallmentStatus, installment.status),
        invoice: installment.invoice && {
          ...installment.invoice,
          status: toEnum(InvoiceStatus, installment.invoice.status),
        },
        payments: installment.payments?.map((payment) => ({
          ...payment,
          method: toEnum(PaymentMethod, payment.method),
          status: toEnum(PaymentStatus, payment.status),
        })),
      })),
    },
  };
}
